/**
 * Workspace Bibliothèque — catalogue de modèles filtrable, chargement
 * explicite, modèles custom (repo Hugging Face), LoRA, VAE.
 *
 * L'état de sélection vit dans le store (selectedModel) ; ce workspace ne
 * garde que les données de catalogue (liste des modèles/LoRA/VAE) et les
 * tags de filtre actifs. Sélectionner une carte ne charge rien : le poids
 * du modèle n'est préparé que sur action explicite ou à la 1re génération.
 */
import { APIClient } from "../../api/client.js";
import { accordion } from "../../components/Accordion.js";
import { confirmDialog } from "../../components/Modal.js";
import { toast } from "../../components/Toast.js";
import { h, setChildren } from "../../components/dom.js";
import { checkboxField, textInputField } from "../../components/fields.js";
import { formatGb } from "../../components/format.js";
import { serverGenerates } from "../../services/execution.js";
import { routeToOwnMachine, waitForHostedResult } from "../../services/hosted-jobs.js";
import { checkHuggingFaceAccess, linkHuggingFace } from "../../services/hugging-face.js";
import type { WorkspaceDef } from "../../shell/types.js";
import { store } from "../../state/store.js";
import type { LoRAInfo, ModelInfo, ModelReadiness, VAEInfo } from "../../types/index.js";
import { componentsPanel } from "./ComponentsPanel.js";

// Les licences nommées par le Hub, telles qu'une personne les lit.
const LICENCE_NAMES: Record<string, string> = {
	"creativeml-openrail-m": "CreativeML OpenRAIL-M",
	"openrail++": "OpenRAIL++",
	"apache-2.0": "Apache 2.0",
	"stabilityai-community-license": "Stability AI Community",
	"flux-1-dev-non-commercial-license": "FLUX.1 [dev], non commercial",
	other: "conditions de l'auteur",
};

const ARCH_BADGES: Record<string, string> = {
	sd15: "SD1.5",
	sdxl: "SDXL",
	sd3: "SD3.5",
	flux: "FLUX",
	pixart_sigma: "PixArt-Σ",
	sana: "Sana",
	zimage: "Z-Image",
};

// Modèles tapés sur le site et pas encore enregistrés par la machine appairée
// (elle le fait au premier job qui les nomme, puis les annonce dans son
// heartbeat). Une commodité de ce navigateur : perdue, on retape le dépôt.
const BROWSER_MODELS_KEY = "library.hubModels";

function readBrowserModels(): ModelInfo[] {
	try {
		const raw = localStorage.getItem(BROWSER_MODELS_KEY);
		const parsed: unknown = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? (parsed as ModelInfo[]) : [];
	} catch {
		return [];
	}
}

function writeBrowserModels(list: ModelInfo[]): void {
	try {
		localStorage.setItem(BROWSER_MODELS_KEY, JSON.stringify(list));
	} catch {
		// Stockage indisponible : la liste vit le temps de la page.
	}
}

/** La licence lisible d'un modèle, "" quand personne ne l'a enregistrée. */
function licenceLabel(model: ModelInfo): string {
	const id = model.license_id || "";
	if (!id || id === "unknown" || id === "unknown-local") return "";
	return LICENCE_NAMES[id] ?? id;
}

function isInstalled(model: ModelInfo): boolean {
	return !!model.is_downloaded || model.type === "local";
}

const USE_LABEL = "Utiliser ce modèle";
const HUB = "https://huggingface.co";

/** Où en est le téléchargement d'un modèle demandé depuis cette page. */
type DownloadState = { phase: "running"; text: string } | { phase: "failed"; text: string };

export const libraryWorkspace: WorkspaceDef = {
	id: "library",
	title: "Bibliothèque",
	icon: "library",
	build(container) {
		container.appendChild(h("h2", { class: "workspace-title" }, "Bibliothèque de modèles"));

		// --- État local (données de catalogue uniquement) ---
		let models: ModelInfo[] = [];
		let browserModels: ModelInfo[] = readBrowserModels();
		const activeTags = new Set<string>();
		let loading = false;
		// Téléchargements lancés d'ici, et modèles arrivés sur la machine depuis
		// que la page est ouverte : la machine ne le redit au serveur qu'à son
		// prochain battement de cœur, la carte n'a pas à l'attendre.
		const downloads = new Map<string, DownloadState>();
		const readyHere = new Set<string>();
		const removedHere = new Set<string>();
		let readinessById = new Map<string, ModelReadiness>();
		// "On the machine" as the machine reported it, when it has: the files
		// step of the readiness answer, the same source as everything else.
		const filesHere = (model: ModelInfo): boolean | null => {
			const files = readinessById.get(model.id)?.steps.find((step) => step.key === "files");
			return files ? files.status === "ok" : null;
		};
		// A server that just started measures sizes in the background: the first
		// listing can lack some, and nothing else would list again.
		let sizesRetried = false;
		const installedHere = (model: ModelInfo): boolean =>
			!removedHere.has(model.id) &&
			(readyHere.has(model.id) || (filesHere(model) ?? isInstalled(model)));

		// --- Barre d'action : utiliser le modèle sélectionné ---
		// /api/models/switch only *selects*: the weights are prepared on the
		// first Generate, in the quantization the planner picks for that
		// resolution (app/api/routes/model.py). The button said "Charger" and
		// the toast "chargé", so the ~30 s of loading then landed, unexplained,
		// on the first image. Say what actually happens.
		const loadButton = h(
			"button",
			{
				class: "btn btn-primary",
				type: "button",
				onclick: () => void loadSelectedModel(),
			},
			USE_LABEL
		);
		const licenseWarning = h("p", { class: "license-warning", hidden: true });
		// Every step for the selected model, done or not: what the card's one
		// line summarises, for whoever wants to see the whole way.
		const checklist = h("ol", { class: "readiness-checklist", hidden: true });
		const actionBar = h(
			"div",
			{ class: "library-action-bar", hidden: true },
			loadButton,
			licenseWarning,
			checklist
		);
		container.appendChild(actionBar);

		// --- Filtres + grille de modèles ---
		const filterRow = h("div", { class: "model-tag-filters" });
		const grid = h("div", { class: "model-grid" });
		container.appendChild(filterRow);
		container.appendChild(grid);

		// --- LoRA et VAE : ceux de la machine qui génère, dans la requête ---
		container.appendChild(componentsPanel());

		// --- Modèle personnalisé : un dépôt Hugging Face ou un .safetensors ---
		// Its architecture is read from its own files, never picked from a list.
		// Locally the server registers it (/api/models/custom); on the hosted
		// site the server only reads it (/api/models/inspect) and the job carries
		// the id to the paired machine, which reads the files again and
		// registers the model itself.
		const customCard = h("div", { class: "card" });
		const localOnly = h("div", { class: "library-local-only", hidden: true });
		const hostedNote = h(
			"p",
			{ class: "help-text library-hosted-note", hidden: true },
			"Les fichiers .safetensors de votre ordinateur apparaissent ici quand vous cochez « Les proposer sur le site » dans le programme."
		);
		container.appendChild(customCard);
		container.appendChild(localOnly);
		container.appendChild(hostedNote);

		const customIdInput = textInputField({
			label: "Dépôt Hugging Face",
			placeholder: "ex. stabilityai/stable-diffusion-xl-base-1.0",
		});
		const customAddButton = h(
			"button",
			{
				class: "btn",
				type: "button",
				onclick: () => void addCustomModel(),
			},
			"Ajouter au catalogue"
		);
		customCard.append(
			h("div", { class: "card-title" }, "Ajouter un modèle personnalisé"),
			h(
				"div",
				{ class: "form-grid" },
				customIdInput.element,
				h(
					"div",
					{ class: "form-group" },
					h("label", { class: "field-label" }, " "),
					customAddButton
				)
			),
			h(
				"p",
				{ class: "help-text" },
				"Un dépôt au format diffusers (org/nom) ou le lien d'un .safetensors SD1.5/SDXL : son architecture est lue dans ses fichiers."
			)
		);

		localOnly.appendChild(
			h(
				"p",
				{ class: "help-text" },
				"Déposez des fichiers .safetensors dans models/checkpoints/ pour ajouter vos propres modèles, dans models/loras/ pour les LoRA."
			)
		);

		// ==================== Rendu ====================

		function renderFilters(): void {
			const tags = new Set<string>();
			// What a model is for, not its family: the family is the badge on
			// every card, and offering both made fourteen filters -- four rows on
			// a phone before the first model.
			for (const model of models) {
				for (const tag of model.tags ?? []) if (tag in TAG_WORDS) tags.add(tag);
			}
			const chips = [...tags]
				.sort((a, b) => tagLabel(a).localeCompare(tagLabel(b), "fr"))
				.map((tag) =>
					h(
						"button",
						{
							class: activeTags.has(tag) ? "tag-chip active" : "tag-chip",
							type: "button",
							onclick: () => {
								if (activeTags.has(tag)) activeTags.delete(tag);
								else activeTags.add(tag);
								renderFilters();
								renderGrid();
							},
						},
						tagLabel(tag)
					)
				);
			setChildren(filterRow, ...chips);
		}

		function renderGrid(): void {
			const visible =
				activeTags.size === 0
					? models
					: models.filter((model) => (model.tags ?? []).some((tag) => activeTags.has(tag)));
			const installed = visible.filter(installedHere);
			const recommended = visible.filter((model) => !installedHere(model));

			const cards: (HTMLElement | string)[] = [];
			if (installed.length > 0) {
				cards.push(h("h4", { class: "model-grid-heading" }, "Prêts à générer"));
				for (const model of installed) cards.push(buildCard(model));
			}
			if (recommended.length > 0) {
				// On the hosted site the download is a button, sent to the paired
				// machine; locally a model still arrives with its first Generate.
				const hosted = !serverGenerates();
				cards.push(
					h(
						"div",
						{ class: "model-grid-heading model-grid-heading-row" },
						h(
							"h4",
							{ class: "model-grid-heading" },
							hosted
								? "À télécharger sur votre ordinateur"
								: "À télécharger à la première génération"
						)
					)
				);
				for (const model of recommended) cards.push(buildCard(model));
			}
			if (cards.length === 0) {
				cards.push(h("p", { class: "muted" }, "Aucun modèle ne correspond aux filtres."));
			}
			setChildren(grid, ...cards);
			updateSelection();
		}

		function buildCard(model: ModelInfo): HTMLElement {
			const arch = ARCH_BADGES[model.architecture] ?? model.architecture;
			const licence = licenceLabel(model);
			// Removable where it was added: the local catalog, or this browser.
			// A model the paired machine registered stays until it forgets it.
			const custom =
				(model.tags ?? []).includes("custom") &&
				(serverGenerates() || browserModels.some((held) => held.id === model.id));

			const footerChildren: HTMLElement[] = [];
			if (licence) {
				const verified = !model.license_id || !!model.license_verified;
				footerChildren.push(
					h("span", { class: verified ? "badge" : "badge badge-warning" }, licence)
				);
			}
			// What stands between the person and this model, as the machine that
			// will do the work reported it (/api/system/readiness): the first step
			// to do and its button, the same answer the form and refusals give.
			const hosted = !serverGenerates();
			// What this page just saw happen beats an answer the machine has not
			// refreshed yet: its next heartbeat still lists what it just changed.
			const answer =
				readyHere.has(model.id) || removedHere.has(model.id)
					? undefined
					: readinessById.get(model.id);
			const download = downloads.get(model.id);
			const busy = download?.phase === "running";
			const nextAction = answer?.next?.action ?? null;
			const offersDownload = answer
				? nextAction === "download"
				: !installedHere(model) && !model.gated;
			const showsDownload = hosted && !busy && (offersDownload || download?.phase === "failed");
			const size =
				model.download_size_gb && model.download_size_gb > 0
					? formatGb(model.download_size_gb, 2)
					: null;
			let statusRow: HTMLElement | null = null;
			if (download) {
				statusRow = h(
					"p",
					{
						class:
							download.phase === "failed"
								? "model-card-download model-card-download-failed"
								: "model-card-download",
						"aria-live": "polite",
					},
					download.text
				);
			} else if (hosted && answer?.next && !(showsDownload && nextAction === "download")) {
				// The download button says it already, size included.
				statusRow = h(
					"p",
					{
						class:
							answer.next.status === "blocked"
								? "model-card-next model-card-next-blocked"
								: "model-card-next",
						title: answer.next.detail ?? "",
					},
					answer.next.title
				);
			}

			// Shown while the licence step is not done, whatever comes first: the
			// order between linking the account and accepting the licence is the
			// person's, and the author's page is where both start.
			const licenceStep = answer?.steps.find((step) => step.key === "licence");
			const licenceLink =
				licenceStep && licenceStep.status !== "ok"
					? licenceStep.link
					: !answer && model.gated
						? `${HUB}/${model.id}`
						: null;
			if (hosted && licenceLink) {
				footerChildren.push(
					h(
						"a",
						{
							class: "btn btn-small model-card-licence-link",
							href: licenceLink,
							target: "_blank",
							rel: "noopener noreferrer",
							title: answer?.next?.detail ?? "Acceptez la licence des auteurs sur Hugging Face.",
							onclick: (event: Event) => event.stopPropagation(),
						},
						"Accepter la licence ↗"
					)
				);
			}
			if (hosted && nextAction === "hf_connect") {
				footerChildren.push(
					h(
						"button",
						{
							class: "btn btn-small btn-primary model-card-hf-link",
							type: "button",
							onclick: (event: Event) => {
								event.stopPropagation();
								void linkHuggingFace().then((account) => {
									if (account) void loadModels();
								});
							},
						},
						"Relier Hugging Face"
					)
				);
			}
			// Once an account is linked, accepting the licence happens on the
			// author's page; this asks the machine to look again.
			const accountLinked = answer?.steps.some(
				(step) => step.key === "hf_account" && step.status === "ok"
			);
			if (hosted && licenceLink && accountLinked) {
				footerChildren.push(
					h(
						"button",
						{
							class: "btn btn-small model-card-hf-check",
							type: "button",
							onclick: (event: Event) => {
								event.stopPropagation();
								void checkHuggingFaceAccess().then((access) => {
									if (!access) return;
									toast(
										access[model.id]
											? `Licence de « ${model.name} » acceptée.`
											: `Hugging Face ne voit pas encore la licence de « ${model.name} » acceptée : vérifiez sur sa page, avec le même compte.`,
										access[model.id] ? "success" : "warning"
									);
									void loadModels();
								});
							},
						},
						"J'ai accepté — vérifier"
					)
				);
			}
			if (showsDownload) {
				footerChildren.push(
					h(
						"button",
						{
							class: "btn btn-small model-card-download-button",
							type: "button",
							onclick: (event: Event) => {
								event.stopPropagation();
								void downloadModel(model);
							},
						},
						download?.phase === "failed"
							? "Réessayer"
							: size
								? `Télécharger · ${size}`
								: "Télécharger"
					)
				);
			}
			if (!showsDownload && size) {
				footerChildren.unshift(h("span", { class: "model-card-size" }, size));
			}
			if (hosted && installedHere(model) && model.type !== "local" && !busy) {
				footerChildren.push(
					h(
						"button",
						{
							class: "btn btn-small model-card-remove-button",
							type: "button",
							title: "Supprimer ce modèle du disque de votre ordinateur",
							onclick: (event: Event) => {
								event.stopPropagation();
								void removeFromMachine(model);
							},
						},
						"Supprimer"
					)
				);
			}

			return h(
				"div",
				{
					class: "model-card",
					role: "option",
					tabindex: "0",
					"aria-selected": "false",
					"data-model-id": model.id,
					onclick: () => store.setState({ selectedModel: model }),
					onkeydown: (event: KeyboardEvent) => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							store.setState({ selectedModel: model });
						}
					},
				},
				h(
					"div",
					{ class: "model-card-title" },
					h("span", { class: "badge badge-primary model-card-arch" }, arch),
					h("span", null, model.name),
					custom
						? h(
								"button",
								{
									class: "model-card-remove",
									type: "button",
									title: "Retirer ce modèle personnalisé",
									"aria-label": "Retirer",
									onclick: (event: Event) => {
										event.stopPropagation();
										void removeCustomModel(model);
									},
								},
								"×"
							)
						: null
				),
				h("p", { class: "model-card-description" }, model.description),
				h(
					"div",
					{ class: "model-card-tags" },
					...(model.tags ?? [])
						.filter((tag) => tag !== model.architecture)
						.map((tag) => h("span", { class: "model-tag" }, tagLabel(tag)))
				),
				statusRow,
				footerChildren.length > 0
					? h("div", { class: "model-card-footer" }, ...footerChildren)
					: null
			);
		}

		function updateSelection(): void {
			const selectedId = store.get().selectedModel?.id ?? null;
			for (const card of grid.querySelectorAll<HTMLElement>(".model-card")) {
				const isSelected = card.dataset.modelId === selectedId;
				card.classList.toggle("selected", isSelected);
				card.setAttribute("aria-selected", String(isSelected));
			}
			const selected = models.find((model) => model.id === selectedId) ?? null;
			actionBar.hidden = selected === null || loading;
			const unverified = selected !== null && !!selected.license_id && !selected.license_verified;
			licenseWarning.hidden = !unverified;
			if (unverified && selected) {
				licenseWarning.textContent = `Licence « ${licenceLabel(selected) || selected.license_id} » non vérifiée — elle sera rappelée au moment de la génération.`;
			}
			const answer = selected ? readinessById.get(selected.id) : undefined;
			checklist.hidden = !answer || serverGenerates();
			setChildren(
				checklist,
				...(answer?.steps ?? []).map((step) =>
					h(
						"li",
						{ class: `readiness-step readiness-${step.status}`, title: step.detail ?? "" },
						`${step.status === "ok" ? "✓" : step.status === "blocked" ? "✕" : "•"} ${step.title}`
					)
				)
			);
		}

		// ==================== Actions ====================

		async function loadSelectedModel(): Promise<void> {
			const model = store.get().selectedModel;
			if (!model || loading) return;
			if (!serverGenerates()) {
				// On the hosted site the choice travels inside the job, to the
				// paired machine that will load it; the server has no model to
				// switch (/api/models/switch is a 404 there, which is what every
				// pick used to report as « Impossible de sélectionner ce modèle »).
				toast(`« ${model.name} » sélectionné.`, "success");
				return;
			}
			loading = true;
			loadButton.disabled = true;
			loadButton.textContent = "Sélection…";
			updateSelection();
			try {
				const success = await APIClient.switchModel(
					model.id,
					store.get().optimizationOptions ?? undefined
				);
				if (success) {
					toast(
						`« ${model.name} » sélectionné : il sera préparé à la première génération.`,
						"success"
					);
				} else {
					toast("Impossible de sélectionner ce modèle.", "error");
				}
			} catch (error) {
				toast(
					error instanceof Error ? error.message : "Impossible de sélectionner ce modèle.",
					"error"
				);
			} finally {
				loading = false;
				loadButton.disabled = false;
				loadButton.textContent = USE_LABEL;
				updateSelection();
			}
		}

		/**
		 * Ask the paired machine to put this model's files on its disk.
		 *
		 * The only download used to be the one hidden inside a first Generate.
		 * Resolves true once the machine says the model is whole there.
		 */
		async function downloadModel(model: ModelInfo): Promise<boolean> {
			const setDownload = (state: DownloadState | null): void => {
				if (state) downloads.set(model.id, state);
				else downloads.delete(model.id);
				renderGrid();
			};
			const target = store.get().executionTarget;
			const workerId = target.kind === "hosted" ? target.worker.worker_id : null;
			setDownload({ phase: "running", text: "Envoi à votre ordinateur…" });
			try {
				const job = await APIClient.submitHostedJob(
					{ model_id: model.id },
					routeToOwnMachine(workerId),
					"model_download"
				);
				await waitForHostedResult(
					job,
					(progress) => {
						const text = progress.preparing
							? progress.message
							: `Téléchargement… ${Math.round(progress.percent)} %`;
						setDownload({ phase: "running", text });
					},
					"Le téléchargement n'a pas abouti."
				);
			} catch (error) {
				setDownload({
					phase: "failed",
					text: error instanceof Error ? error.message : "Le téléchargement n'a pas abouti.",
				});
				return false;
			}
			readyHere.add(model.id);
			removedHere.delete(model.id);
			setDownload(null);
			toast(`« ${model.name} » est prêt sur votre ordinateur.`, "success");
			return true;
		}

		/** Free the paired machine's disk of this model, after asking. */
		async function removeFromMachine(model: ModelInfo): Promise<void> {
			const size = model.download_size_gb ? ` (environ ${model.download_size_gb} Go)` : "";
			const ok = await confirmDialog(
				"Supprimer de votre ordinateur",
				`Supprimer « ${model.name} »${size} du disque de votre ordinateur ? Vous pourrez le retélécharger depuis cette page.`
			);
			if (!ok) return;
			const target = store.get().executionTarget;
			const workerId = target.kind === "hosted" ? target.worker.worker_id : null;
			downloads.set(model.id, { phase: "running", text: "Suppression…" });
			renderGrid();
			try {
				const job = await APIClient.submitHostedJob(
					{ model_id: model.id },
					routeToOwnMachine(workerId),
					"model_delete"
				);
				const result = await waitForHostedResult(job, () => {}, "La suppression n'a pas abouti.");
				downloads.delete(model.id);
				readyHere.delete(model.id);
				removedHere.add(model.id);
				renderGrid();
				// The machine names the repo; the person knows the card's name.
				// When nothing was freed (a model another program holds), the
				// machine's own sentence is the one that explains why.
				const freed = Number(result.data?.freed_gb ?? 0);
				const said = result.data?.message;
				toast(
					freed > 0
						? `« ${model.name} » a été supprimé de votre ordinateur (${formatGb(freed)} libérés).`
						: typeof said === "string"
							? said
							: `« ${model.name} » a été retiré de votre ordinateur.`,
					"success"
				);
			} catch (error) {
				downloads.set(model.id, {
					phase: "failed",
					text: error instanceof Error ? error.message : "La suppression n'a pas abouti.",
				});
				renderGrid();
			}
		}

		async function addCustomModel(): Promise<void> {
			const modelId = customIdInput.getValue().trim();
			if (!modelId) {
				toast("Indiquez le dépôt Hugging Face du modèle.", "warning");
				return;
			}
			const already = models.find((model) => model.id === modelId);
			if (already) {
				store.setState({ selectedModel: already });
				toast("Ce modèle est déjà dans la bibliothèque.", "info");
				return;
			}
			customAddButton.disabled = true;
			try {
				const here = serverGenerates();
				const added = here
					? await APIClient.addCustomModel(modelId)
					: await APIClient.inspectModel(modelId);
				if (!here) {
					browserModels = [...browserModels.filter((m) => m.id !== added.id), added];
					writeBrowserModels(browserModels);
				}
				await loadModels();
				store.setState({
					selectedModel: models.find((model) => model.id === added.id) ?? null,
				});
				customIdInput.setValue("");
				const arch = ARCH_BADGES[added.architecture] ?? added.architecture;
				toast(
					here
						? `« ${added.name} » ajouté (${arch}, lu dans ses fichiers).`
						: `« ${added.name} » (${arch}, lu dans ses fichiers) : votre ordinateur l'enregistrera à la première génération.`,
					"success"
				);
			} catch (error) {
				toast(error instanceof Error ? error.message : "Échec de l'ajout du modèle.", "error");
			} finally {
				customAddButton.disabled = false;
			}
		}

		async function removeCustomModel(model: ModelInfo): Promise<void> {
			const ok = await confirmDialog(
				"Retirer le modèle",
				`Retirer « ${model.name} » du catalogue ?`
			);
			if (!ok) return;
			try {
				if (browserModels.some((held) => held.id === model.id)) {
					browserModels = browserModels.filter((held) => held.id !== model.id);
					writeBrowserModels(browserModels);
				} else {
					await APIClient.removeCustomModel(model.id);
				}
				if (store.get().selectedModel?.id === model.id) {
					store.setState({ selectedModel: null });
				}
				await loadModels();
			} catch (error) {
				toast(
					error instanceof Error ? error.message : "Échec de la suppression du modèle.",
					"error"
				);
			}
		}

		// ==================== Chargement des données ====================

		async function loadModels(): Promise<void> {
			try {
				const listed = await APIClient.getModels();
				// Once the paired machine announces a model, the server lists it
				// and this browser can let go of its own copy.
				const known = new Set(listed.map((model) => model.id));
				if (browserModels.some((held) => known.has(held.id))) {
					browserModels = browserModels.filter((held) => !known.has(held.id));
					writeBrowserModels(browserModels);
				}
				models = serverGenerates() ? listed : [...browserModels, ...listed];
			} catch (error) {
				toast(error instanceof Error ? error.message : "Liste des modèles indisponible.", "error");
				return;
			}
			// Un modèle disparu du catalogue ne doit pas rester sélectionné.
			const selectedId = store.get().selectedModel?.id ?? null;
			if (selectedId && !models.some((model) => model.id === selectedId)) {
				store.setState({ selectedModel: null });
			}
			await loadReadiness();
			renderFilters();
			renderGrid();
			const sizeMissing = models.some(
				(model) => model.type !== "local" && !(model.download_size_gb && model.download_size_gb > 0)
			);
			if (sizeMissing && !sizesRetried) {
				sizesRetried = true;
				window.setTimeout(() => void loadModels(), 15_000);
			}
		}

		/** Best effort: without an answer the cards keep their own defaults. */
		async function loadReadiness(): Promise<void> {
			try {
				const answers = await APIClient.getReadiness();
				// The family entries (video, voice, tools) belong to those pages; the
				// same SD 1.5 repo appears there as AnimateDiff's base.
				readinessById = new Map(
					answers.filter((answer) => !answer.family).map((answer) => [answer.model_id, answer])
				);
			} catch {
				readinessById = new Map();
			}
		}

		// ==================== Abonnements store ====================

		// Le statut (pollé côté shell) révèle le modèle effectivement chargé :
		// on coche sa carte, y compris au démarrage.
		store.subscribe(
			(s) => s.status?.model.id ?? null,
			(loadedId) => {
				if (!loadedId) return;
				const loaded = models.find((model) => model.id === loadedId);
				if (loaded && store.get().selectedModel?.id !== loaded.id) {
					store.setState({ selectedModel: loaded });
				} else {
					updateSelection();
				}
			}
		);

		// La sélection (carte cliquée, statut, autre workspace) pilote la
		// barre d'action ; le moteur du modèle choisi pilote le panneau LoRA.
		store.subscribe(
			(s) => s.selectedModel,
			() => updateSelection()
		);
		// Local-only settings follow /api/status: unknown is not yes, so the
		// page opens with neither block and picks one once the status is in.
		// subscribe() only hears changes, so the status already in the store
		// when this workspace is built is applied once by hand.
		function showLocalSettings(generatesHere: boolean | null): void {
			localOnly.hidden = generatesHere !== true;
			hostedNote.hidden = generatesHere !== false;
		}
		store.subscribe((s) => s.status?.generates_here ?? null, showLocalSettings);
		showLocalSettings(store.get().status?.generates_here ?? null);

		// The listing answers for the caller's own machines (their files, the
		// repos they registered, what is already downloaded there). It was
		// fetched once, before sign-in, and never again: a machine's models
		// only showed after some other action happened to reload it. A string
		// key, so the heartbeat's fresh arrays do not reload it every poll.
		store.subscribe(
			(s) => s.user?.user_id ?? null,
			() => void loadModels()
		);
		store.subscribe(
			(s) =>
				s.workers
					.filter((worker) => worker.online)
					.map((worker) =>
						[
							worker.worker_id,
							...(worker.local_models ?? []).map((model) => model.id),
							...(worker.hub_models ?? []).map((model) => model.id),
							...Object.keys(worker.cached_models_gb ?? {}),
							// Un modèle qui finit de se télécharger ne fait apparaître
							// aucune clé neuve : c'est sa disparition d'ici qui le dit.
							...Object.keys(worker.incomplete_models ?? {}),
							// Relier Hugging Face ou mettre le programme à jour change
							// ce qui reste à faire, sans rien changer d'autre.
							`hf:${worker.hf_account ?? ""}`,
							`build:${worker.agent_build ?? 0}`,
						].join(",")
					)
					.sort()
					.join("|"),
			() => void loadModels()
		);

		// Chargement initial.
		void loadModels();
	},
};

// What the catalogue's tags mean to a person. A family tag (sd15, sdxl…) is
// already the card's badge, so the card does not repeat it; the filters show
// the family by its badge name.
const TAG_WORDS: Record<string, string> = {
	anime: "Anime",
	custom: "Ajouté par vous",
	edit: "Retouche d'image",
	fast: "Rapide",
	"general-purpose": "Polyvalent",
	lightweight: "Léger",
	photorealistic: "Photo réaliste",
	quality: "Haute qualité",
};

function tagLabel(tag: string): string {
	return TAG_WORDS[tag] ?? ARCH_BADGES[tag] ?? tag;
}
